import { useManagementGetCompanies } from '@apps-common/apis/management/company/get-companies'
import { useManagementGetProducts } from '@apps-common/apis/management/product/get-products'
import { useManagementSearchSimApns } from '@apps-common/apis/management/sim-apn'
import { useManagementSearchSimTypes } from '@apps-common/apis/management/sim-type/search-sim-types'
import { useManagementSearchUnitFirmwares } from '@apps-common/apis/management/unit-firmware/search-unit-firmwares'
import { useManagementSearchUnitPlans } from '@apps-common/apis/management/unit-plan/get-unit-plans'
import { IUnitManagementSelectOption } from '@apps-common/types/form'

export interface IManagementUnitSelectItem {
  defaultValue: string
  options: IUnitManagementSelectOption[]
  refetch: any
}

export interface IManagementUnitSelectItems {
  isLoading: boolean
  companies: IManagementUnitSelectItem
  products: IManagementUnitSelectItem
  plans: IManagementUnitSelectItem
  firmwares: IManagementUnitSelectItem
  simApns: IManagementUnitSelectItem
  simTypes: IManagementUnitSelectItem
}

export const useManagementUnitSelectItems = (): IManagementUnitSelectItems => {
  const {
    data: companies,
    isLoading: companiesLoading,
    isError: companiesError,
    refetch: companiesRefetch
  } = useManagementGetCompanies({
    config: {
      staleTime: 60000
    },
    name: ''
  })

  const {
    data: products,
    isLoading: productsLoading,
    isError: productsError,
    refetch: productsRefetch
  } = useManagementGetProducts({
    config: {
      staleTime: 60000
    },
    name: ''
  })

  const {
    data: plans,
    isLoading: plansLoading,
    isError: plansError,
    refetch: plansRefetch
  } = useManagementSearchUnitPlans({
    config: {
      staleTime: 60000
    }
  })

  const {
    data: firmwares,
    isLoading: firmwaresLoading,
    isError: firmwaresError,
    refetch: firmwaresRefetch
  } = useManagementSearchUnitFirmwares({
    config: {
      staleTime: 60000
    }
  })

  const {
    data: simApns,
    isLoading: simApnsLoading,
    isError: simApnsError,
    refetch: simApnsRefetch
  } = useManagementSearchSimApns({
    config: {
      staleTime: 60000
    }
  })

  const {
    data: simTypes,
    isLoading: simTypesLoading,
    isError: simTypesError,
    refetch: simTypesRefetch
  } = useManagementSearchSimTypes({
    config: {
      staleTime: 60000
    }
  })

  const isLoading =
    companiesLoading ||
    companiesError ||
    productsLoading ||
    productsError ||
    plansLoading ||
    plansError ||
    firmwaresLoading ||
    firmwaresError ||
    simApnsLoading ||
    simApnsError ||
    simTypesLoading ||
    simTypesError

  const defaultItem: IManagementUnitSelectItem = {
    defaultValue: '',
    options: [],
    refetch: () => {}
  }

  return isLoading
    ? {
        isLoading,
        companies: defaultItem,
        products: defaultItem,
        plans: defaultItem,
        firmwares: defaultItem,
        simApns: defaultItem,
        simTypes: defaultItem
      }
    : {
        isLoading,
        companies: {
          defaultValue: '',
          options:
            companies?.map((c) => ({ label: c.companyName, value: c.companyId, altDefaultValue: c.defaultMonitoringCentreId })) || [],
          refetch: companiesRefetch
        },
        products: {
          defaultValue: products?.find((p) => p.defaultItem)?.productId || '',
          options: products?.map((p) => ({ label: p.productName, value: p.productId })) || [],
          refetch: productsRefetch
        },
        plans: {
          defaultValue: plans?.find((p) => p.defaultItem)?.unitPlanId || '',
          options: plans?.map((p) => ({ label: p.unitPlanName, value: p.unitPlanId })) || [],
          refetch: plansRefetch
        },
        firmwares: {
          defaultValue: firmwares?.find((f) => f.defaultItem)?.unitFirmwareId || '',
          options: firmwares?.map((f) => ({ label: f.unitFirmwareVersion, value: f.unitFirmwareId })) || [],
          refetch: firmwaresRefetch
        },
        simApns: {
          defaultValue: simApns?.find((s) => s.defaultItem)?.simApnId || '',
          options: simApns?.map((s) => ({ label: s.simApnName, value: s.simApnId })) || [],
          refetch: simApnsRefetch
        },
        simTypes: {
          defaultValue: simTypes?.find((s) => s.defaultItem)?.simTypeId || '',
          options: simTypes?.map((s) => ({ label: s.simTypeName, value: s.simTypeId })) || [],
          refetch: simTypesRefetch
        }
      }
}
